import { Classification, ClassificationSection } from "@/src/models/constants/classifications";
import { Workflow, WorkflowType } from "@/src/models/constants/group";
import { ServiceLine } from "@/src/models/constants/service-lines";
import { Group } from "@/src/models/group";

// Intake uses these dates when the patient's DOB is unknown.
export const FCC_PATIENT_LOOKUP_PLACEHOLDER_DATES: readonly string[] = ["1900-01-01", "1901-01-01"];

const FCC_CLASSIFICATIONS_FOR_DISPLAY = [
  Classification.BLANK,
  Classification.COVER_PAGE,
  Classification.INCOMPLETE,
  Classification.INSURANCE_CARD,
  Classification.JUNK,
  Classification.LAB_RESULTS,
  Classification.MEDICAL_HISTORY,
  Classification.OTHER,
  Classification.PROGRESS_NOTE,
  Classification.REFERRAL,
  Classification.REFERRAL_REQUEST,
  Classification.UNKNOWN,
] as Classification[];

export const FCC_DOC_ROUTING_LABEL_BY_CLASSIFICATION: Partial<Record<Classification, string>> = {
  [Classification.LAB_RESULTS]: "Lab Results",
  [Classification.PROGRESS_NOTE]: "Progress Note",
  [Classification.MEDICAL_HISTORY]: "Medical Summary",
  [Classification.INSURANCE_CARD]: "Insurance Card",
};

export function getFccClassificationsToExtract(): Classification[] {
  return [
    Classification.REFERRAL,
    Classification.REFERRAL_REQUEST,
    Classification.LAB_RESULTS,
    Classification.PROGRESS_NOTE,
    Classification.MEDICAL_HISTORY,
    Classification.INSURANCE_CARD,
  ];
}

export function getFccClassificationsToDisplayForSelection(): ClassificationSection[] {
  return [
    {
      label: "Classification",
      options: FCC_CLASSIFICATIONS_FOR_DISPLAY,
    },
  ];
}

export function getFccWorkflows(_group: Group): Partial<Record<WorkflowType, Classification[]>> {
  return {
    [Workflow.REFERRAL]: [Classification.REFERRAL],
    [Workflow.DOC_MANAGEMENT]: [
      Classification.REFERRAL_REQUEST,
      Classification.LAB_RESULTS,
      Classification.PROGRESS_NOTE,
      Classification.MEDICAL_HISTORY,
      Classification.INSURANCE_CARD,
    ],
  };
}

export const FCC_CC_SERVICE_LINES: ReadonlySet<ServiceLine> = new Set([
  ServiceLine.MED_MANAGEMENT,
  ServiceLine.THERAPY,
  ServiceLine.MED_MANAGEMENT_AND_THERAPY,
]);

export const FCC_SPECIALTY_INBOX_EMAIL = "specialtyservicesintake@fccsprings.com";
export const FCC_VA_MILITARY_INBOX_EMAIL = "fccauth@fccsprings.com";

export const FCC_USER_ID: string[] = [
  "0a40227e-64ef-4dbc-943b-d712a59f1d83",
  "61e1bfa4-ebdd-447e-98dd-f3aae02d7b17",
  "c2992751-4b8f-413c-a653-fa5a8a5e13aa",
  "5475fddf-55ae-4244-b142-5a32e7a99f37",
  "7f5d716b-8b37-4372-8800-b30074f484aa",
  "3fb61ad6-9892-498e-b319-362db582018f", // Sibel FCC Login 1
  "d146b484-c0b3-4187-9c7d-8923e41517a5", // Sibel FCC Login 2
  "fbffe2ee-f22f-408f-a67f-1f1c72d545d6", // Sibel FCC Login 3
  "f6c9dada-6450-403b-abd1-cdb5d146b70e", // Sibel FCC Login 4
  "27b4638e-aa3c-49a9-93bc-42a9834c5329", // Sibel FCC Login 5
  "4228b039-0f1f-4b64-b410-37b099715b18", // Sibel FCC Login 6
  "4a14f128-00e8-48e0-8f97-d94511e49c29", // Sean FCC Login 6
  "a9675858-1886-47a2-ac31-4db807af88e9", // Jack FCC Login 1
  "a0320605-4d3e-4fe0-ba74-97852bdc2ede", // Jack FCC Login 2
  "db4f6ef6-bd00-4fe7-bf7a-37d788cb1888", // Jack FCC Login 3
  "aef98311-dc3c-40c6-9cda-b76d75d5f902", // Jack FCC Login 4
  "565f61f3-4967-48a7-9329-17fbaec6be8b", // Jack FCC Login 5
  "0a8576af-4a75-40f2-9646-f7c29f504cce", // Ashley FCC Login 1
  "0f9906c3-ca3c-456a-ad7a-d7ba20bcb4ae", // Ashley FCC Login 2
  "74da66c8-0321-4c9a-a55a-cb1cdb97d665", // Ashley FCC Login 3
  "e438abf0-4618-4716-ac51-534a18bb1f05", // Ashley FCC Login 4
  "84a7bdac-ba38-4a67-a03c-f809371cae91", // Ashley FCC Login 5
  "06fa9656-367b-4d1c-9b6e-142a696bddfc", // Anna FCC Login 1
  "109de79d-d435-4a78-9bc2-386b9e3ba4df", // Anna FCC Login 2
  "a745b734-7d3e-4356-9636-b6ed9939b19d", // Anna FCC Login 3
  "d439a6e4-22a7-4d98-a121-04896240f3e4", // Anna FCC Login 4
  "4ed0252c-0316-4199-b3de-149c9721d1be", // Anna FCC Login 5
];

export const FCC_SERVICE_LINE_FILENAME_ABBR: Partial<Record<ServiceLine, string>> = {
  [ServiceLine.MED_MANAGEMENT]: "MM",
  [ServiceLine.THERAPY]: "TH",
  [ServiceLine.MED_MANAGEMENT_AND_THERAPY]: "MM TH",
};

const FCC_REFERRAL_REGION_BY_STATE: Record<string, string> = {
  AZ: "Arizona",
  CO: "CO",
  FL: "FL Tampa",
  TN: "Tennessee",
};

const FCC_OUTBOUND_DIALER_BY_STATE: Record<string, string> = {
  AZ: "Ref OB-AZ 602-898-9717",
  CO: "Ref OB-CO 720-896-7713",
  FL: "Ref OB-FL 813-803-6135",
  TN: "Ref OB-TN 615-931-3199",
};

// TX splits into two regions by the referring zip's first 3 digits: these prefixes
// cover San Antonio + its southern/southwestern orbit (210/726/830 area-code
// footprint); every other TX zip (incl. Austin and missing) falls through to Austin.
const FCC_TX_SAN_ANTONIO_ZIP_PREFIXES = new Set(["782", "780", "781", "788"]);

const FCC_TX_AUSTIN_REGION = "TX Austin";
const FCC_TX_SAN_ANTONIO_REGION = "TX San Antonio";
const FCC_TX_AUSTIN_DIALER = "Ref OB-TX Austin All Other Area Codes 737-910-1217";
const FCC_TX_SAN_ANTONIO_DIALER = "Ref OB-TX San Ant 210:726:830 210-942-3513";

function isFccTxSanAntonioZip(postalCode: string | null | undefined): boolean {
  return FCC_TX_SAN_ANTONIO_ZIP_PREFIXES.has((postalCode ?? "").trim().slice(0, 3));
}

// CO routes by referring zip to three regions matching the CloudCruise
// clinical-form dropdown:
//   - 808/809 → CO Colorado Springs (El Paso + Teller County: Colorado
//     Springs proper, USAFA, Manitou Springs, Woodland Park, Fountain,
//     plus FCC-listed Briargate, Circle, Interquest, Marksheffel,
//     Parkmoor Village).
//   - 801 → CO South Denver (Arapahoe + Douglas County south metro:
//     Englewood, Littleton (incl. West Littleton), Centennial, Highlands
//     Ranch, Castle Rock, Parker, Lone Tree).
//   - 800 / 803 / 804 / 805 / 806 → CO North Denver (Broomfield,
//     Westminster, Arvada, Commerce City, Aurora, Boulder metro,
//     Golden/foothills, Fort Collins, Loveland, Longmont, Brighton,
//     Louisville).
// 802xx (Denver city proper + inner suburbs) can't be routed by prefix
// because Colfax splits the range across both metros — it uses the
// FCC_CO_ZIP_802_OVERRIDES map below. Anything else (807xx, 81xxx
// Western Slope / Pueblo, missing zips) falls through to plain "CO",
// which CloudCruise treats as Colorado Springs.
const FCC_CO_COLORADO_SPRINGS_ZIP_PREFIXES = new Set(["808", "809"]);
const FCC_CO_SOUTH_DENVER_ZIP_PREFIXES = new Set(["801"]);
const FCC_CO_NORTH_DENVER_ZIP_PREFIXES = new Set(["800", "803", "804", "805", "806"]);

const FCC_CO_COLORADO_SPRINGS_REGION = "CO Colorado Springs";
const FCC_CO_SOUTH_DENVER_REGION = "CO South Denver";
const FCC_CO_NORTH_DENVER_REGION = "CO North Denver";

// Neutral sub-region key, shared by getFccCoRegion (referral form, "CO "-prefixed
// labels) and getFccInteractionCoRegion (interaction form, bare labels) so the
// zip classification below (including the FCC-confirmed 802xx override map) is
// defined once and only the output label differs per consumer. Referenced via
// FccCoSubRegion.<KEY> everywhere (never as a bare string) so a typo in any one
// spot is a compile error instead of a silently-wrong region.
const FccCoSubRegion = {
  COLORADO_SPRINGS: "COLORADO_SPRINGS",
  SOUTH_DENVER: "SOUTH_DENVER",
  NORTH_DENVER: "NORTH_DENVER",
} as const;

type FccCoSubRegion = (typeof FccCoSubRegion)[keyof typeof FccCoSubRegion];

// 802xx spans both metros: Central Park (80238) and Sloan's Lake
// (80204/80212) are North per FCC's city guide; Lowry (80230/80237) is
// South. The remaining 802xx zips aren't on FCC's list, so they are
// approximated using Colfax (~E 15th Ave) as the divide — north of
// Colfax → North Denver, south → South Denver. Revise any entry FCC
// disagrees with; the three misrouted tasks that motivated this map
// (Pope 80220 → South, Vieth 80233 → North, Pryor 80227 → South) all
// fall in the approximated set.
const FCC_CO_ZIP_802_OVERRIDES: Record<string, FccCoSubRegion> = {
  // North Denver — FCC-confirmed
  "80204": FccCoSubRegion.NORTH_DENVER, // Sloan's Lake / Sun Valley
  "80212": FccCoSubRegion.NORTH_DENVER, // Sloan's Lake / Edgewater / West Highland
  "80234": FccCoSubRegion.NORTH_DENVER, // Northglenn
  "80238": FccCoSubRegion.NORTH_DENVER, // Central Park / Stapleton
  // North Denver — approximated (north of Colfax + north metro suburbs)
  "80202": FccCoSubRegion.NORTH_DENVER, // LoDo / downtown
  "80205": FccCoSubRegion.NORTH_DENVER, // Five Points / Whittier / Cole
  "80207": FccCoSubRegion.NORTH_DENVER, // Park Hill
  "80211": FccCoSubRegion.NORTH_DENVER, // Highlands / LoHi / Berkeley / Sunnyside
  "80214": FccCoSubRegion.NORTH_DENVER, // Edgewater / Lakewood NE
  "80215": FccCoSubRegion.NORTH_DENVER, // Lakewood (north central)
  "80216": FccCoSubRegion.NORTH_DENVER, // Globeville / Elyria-Swansea
  "80221": FccCoSubRegion.NORTH_DENVER, // Federal Heights / N Westminster / Sherrelwood
  "80229": FccCoSubRegion.NORTH_DENVER, // Thornton
  "80233": FccCoSubRegion.NORTH_DENVER, // Thornton / Northglenn
  "80239": FccCoSubRegion.NORTH_DENVER, // Montbello
  "80241": FccCoSubRegion.NORTH_DENVER, // Thornton
  "80249": FccCoSubRegion.NORTH_DENVER, // Green Valley Ranch
  "80260": FccCoSubRegion.NORTH_DENVER, // Thornton / Federal Heights
  // South Denver — FCC-confirmed
  "80230": FccCoSubRegion.SOUTH_DENVER, // Lowry
  "80237": FccCoSubRegion.SOUTH_DENVER, // Southmoor / Hampden (Lowry adjacent)
  // South Denver — approximated (south of Colfax)
  "80203": FccCoSubRegion.SOUTH_DENVER, // Capitol Hill / Speer
  "80206": FccCoSubRegion.SOUTH_DENVER, // Cherry Creek / Congress Park
  "80209": FccCoSubRegion.SOUTH_DENVER, // Wash Park / Platt Park
  "80210": FccCoSubRegion.SOUTH_DENVER, // University / DU / Rosedale
  "80218": FccCoSubRegion.SOUTH_DENVER, // Cheesman / Alamo Placita
  "80219": FccCoSubRegion.SOUTH_DENVER, // Westwood / Barnum / Ruby Hill
  "80220": FccCoSubRegion.SOUTH_DENVER, // Hale / Hilltop / Mayfair / Montclair
  "80222": FccCoSubRegion.SOUTH_DENVER, // Virginia Village / University Hills
  "80223": FccCoSubRegion.SOUTH_DENVER, // Athmar Park / West Wash Park
  "80224": FccCoSubRegion.SOUTH_DENVER, // Virginia Vale / Windsor
  "80226": FccCoSubRegion.SOUTH_DENVER, // Lakewood (central / Belmar)
  "80227": FccCoSubRegion.SOUTH_DENVER, // Bear Valley / SW Denver
  "80228": FccCoSubRegion.SOUTH_DENVER, // Green Mountain / Lakewood west
  "80231": FccCoSubRegion.SOUTH_DENVER, // Windsor Gardens / Hampden
  "80232": FccCoSubRegion.SOUTH_DENVER, // Lakewood (south)
  "80235": FccCoSubRegion.SOUTH_DENVER, // Pinehurst / Marston
  "80236": FccCoSubRegion.SOUTH_DENVER, // Harvey Park
};

function getFccCoSubRegion(postalCode: string | null | undefined): FccCoSubRegion | null {
  const trimmed = (postalCode ?? "").trim();
  const zip5 = trimmed.slice(0, 5);
  const prefix3 = trimmed.slice(0, 3);

  const zipOverride = FCC_CO_ZIP_802_OVERRIDES[zip5];

  if (zipOverride !== undefined) return zipOverride;

  if (FCC_CO_COLORADO_SPRINGS_ZIP_PREFIXES.has(prefix3)) return FccCoSubRegion.COLORADO_SPRINGS;
  if (FCC_CO_SOUTH_DENVER_ZIP_PREFIXES.has(prefix3)) return FccCoSubRegion.SOUTH_DENVER;
  if (FCC_CO_NORTH_DENVER_ZIP_PREFIXES.has(prefix3)) return FccCoSubRegion.NORTH_DENVER;

  return null;
}

function getFccCoRegion(postalCode: string | null | undefined): string | null {
  switch (getFccCoSubRegion(postalCode)) {
    case FccCoSubRegion.COLORADO_SPRINGS:
      return FCC_CO_COLORADO_SPRINGS_REGION;
    case FccCoSubRegion.SOUTH_DENVER:
      return FCC_CO_SOUTH_DENVER_REGION;
    case FccCoSubRegion.NORTH_DENVER:
      return FCC_CO_NORTH_DENVER_REGION;
    default:
      return null;
  }
}

// AZ routes by referring zip to three regions matching the CloudCruise
// clinical-form dropdown (source: FCC-provided AZ region/zip spreadsheet,
// plus a set of geography-inferred entries noted below — pending FCC
// confirmation, but shipped to close known gaps in the low-volume 850/851/856xx range).
// 852xx (Mesa/Gilbert/Chandler), 853xx (Peoria/Goodyear), and 857xx
// (Tucson) each fall entirely within one region in the source data, so
// those are handled as prefix Sets like CO's clean prefixes. 850xx and
// 851xx are split between West and East Valley (e.g. 85008/85044 Phoenix
// area is East Valley but 85086/85087 is West Valley), and 856xx is mostly
// rural southeastern AZ (Bisbee, Douglas, Sierra Vista) rather than Tucson
// metro, so those ranges use an explicit override map instead of assuming
// the rest of the prefix — same approach as CO's ambiguous 802xx. Any AZ
// zip not covered here (Phoenix proper, Flagstaff, Yuma, missing zip,
// etc.) falls through to the plain "Arizona" region via
// FCC_REFERRAL_REGION_BY_STATE.
const FCC_AZ_WEST_VALLEY_REGION = "AZ - West Valley";
const FCC_AZ_EAST_VALLEY_REGION = "AZ - East Valley";
const FCC_AZ_TUCSON_REGION = "AZ - Tucson";

const FCC_AZ_EAST_VALLEY_ZIP_PREFIXES = new Set(["852"]);
const FCC_AZ_WEST_VALLEY_ZIP_PREFIXES = new Set(["853"]);
const FCC_AZ_TUCSON_ZIP_PREFIXES = new Set(["857"]);

const FCC_AZ_ZIP_OVERRIDES: Record<string, string> = {
  "85008": FCC_AZ_EAST_VALLEY_REGION, // Phoenix (Tempe-adjacent)
  "85044": FCC_AZ_EAST_VALLEY_REGION, // Ahwatukee
  "85086": FCC_AZ_WEST_VALLEY_REGION, // New River / Desert Hills (Peoria)
  "85087": FCC_AZ_WEST_VALLEY_REGION, // New River / Desert Hills (Peoria)
  "85118": FCC_AZ_EAST_VALLEY_REGION, // Gold Canyon — geography-inferred, pending FCC confirmation
  "85119": FCC_AZ_EAST_VALLEY_REGION, // Apache Junction — geography-inferred, pending FCC confirmation
  "85120": FCC_AZ_EAST_VALLEY_REGION, // Apache Junction
  "85138": FCC_AZ_WEST_VALLEY_REGION, // Maricopa — geography-inferred (consistent with 85139), pending FCC confirmation
  "85139": FCC_AZ_WEST_VALLEY_REGION, // Maricopa (Goodyear)
  "85140": FCC_AZ_EAST_VALLEY_REGION, // San Tan Valley
  "85142": FCC_AZ_EAST_VALLEY_REGION, // Queen Creek
  "85143": FCC_AZ_EAST_VALLEY_REGION, // San Tan Valley — geography-inferred, pending FCC confirmation
  "85614": FCC_AZ_TUCSON_REGION, // Green Valley — geography-inferred, pending FCC confirmation
  "85641": FCC_AZ_TUCSON_REGION, // Sahuarita
  "85653": FCC_AZ_TUCSON_REGION, // Marana — geography-inferred, pending FCC confirmation
};

function getFccAzRegion(postalCode: string | null | undefined): string | null {
  const trimmed = (postalCode ?? "").trim();
  const zip5 = trimmed.slice(0, 5);
  const prefix3 = trimmed.slice(0, 3);

  const zipOverride = FCC_AZ_ZIP_OVERRIDES[zip5];

  if (zipOverride !== undefined) return zipOverride;

  if (FCC_AZ_EAST_VALLEY_ZIP_PREFIXES.has(prefix3)) return FCC_AZ_EAST_VALLEY_REGION;
  if (FCC_AZ_WEST_VALLEY_ZIP_PREFIXES.has(prefix3)) return FCC_AZ_WEST_VALLEY_REGION;
  if (FCC_AZ_TUCSON_ZIP_PREFIXES.has(prefix3)) return FCC_AZ_TUCSON_REGION;

  return null;
}

export function getFccReferralRegion(state: string | null | undefined, postalCode?: string | null): string {
  const normalizedState = (state ?? "").trim().toUpperCase();

  if (normalizedState === "TX") {
    return isFccTxSanAntonioZip(postalCode) ? FCC_TX_SAN_ANTONIO_REGION : FCC_TX_AUSTIN_REGION;
  }

  if (normalizedState === "CO") {
    const coRegion = getFccCoRegion(postalCode);

    if (coRegion !== null) return coRegion;
  }

  if (normalizedState === "AZ") {
    const azRegion = getFccAzRegion(postalCode);

    if (azRegion !== null) return azRegion;
  }

  return FCC_REFERRAL_REGION_BY_STATE[normalizedState] ?? "Other";
}

export function getFccOutboundDialer(state: string | null | undefined, postalCode?: string | null): string {
  const normalizedState = (state ?? "").trim().toUpperCase();

  if (normalizedState === "TX") {
    return isFccTxSanAntonioZip(postalCode) ? FCC_TX_SAN_ANTONIO_DIALER : FCC_TX_AUSTIN_DIALER;
  }

  return FCC_OUTBOUND_DIALER_BY_STATE[normalizedState] ?? "";
}

// interaction_region (for the FCC clinical-form CloudCruise dropdown) is derived from the
// patient's own home address rather than the referring/receiving clinic location, and maps
// to a fixed 9-value enum with no "Other" bucket — an unmapped state/zip fails closed rather
// than emitting an invalid dropdown value. The TMS service line uses different TN labels
// for the same zip split; see getFccInteractionTnRegion.
const FCC_INTERACTION_REGION_BY_STATE: Partial<Record<string, string>> = {
  AZ: "Arizona",
  FL: "Tampa",
};

function getFccInteractionCoRegion(postalCode: string | null | undefined): string | null {
  switch (getFccCoSubRegion(postalCode)) {
    case FccCoSubRegion.COLORADO_SPRINGS:
      return "Colorado Springs";
    case FccCoSubRegion.SOUTH_DENVER:
      return "South Denver";
    case FccCoSubRegion.NORTH_DENVER:
      return "North Denver";
    default:
      return null;
  }
}

// TN splits by the patient zip's first 3 digits, covering the full standard USPS TN zip3
// range (370-385): 370-379 (Nashville/Chattanooga/Knoxville) -> South Tennessee, 380-385
// (Memphis/Jackson) -> North Tennessee. Proposed boundary pending FCC confirmation -- revise
// the two prefix sets below if FCC provides a different split (same pattern as the CO/AZ
// override maps above).
const FCC_SOUTH_TENNESSEE_ZIP_PREFIXES = new Set([
  "370",
  "371",
  "372",
  "373",
  "374",
  "375",
  "376",
  "377",
  "378",
  "379",
]);
const FCC_NORTH_TENNESSEE_ZIP_PREFIXES = new Set(["380", "381", "382", "383", "384", "385"]);

// TMS uses its own labels for the same TN zip split ("TN South"/"TN North" instead of
// "South Tennessee"/"North Tennessee") -- every other service line keeps the long form.
function getFccInteractionTnRegion(
  postalCode: string | null | undefined,
  serviceLine: ServiceLine | null
): string | null {
  const prefix3 = (postalCode ?? "").trim().slice(0, 3);
  const isTms = serviceLine === ServiceLine.TMS;

  if (FCC_SOUTH_TENNESSEE_ZIP_PREFIXES.has(prefix3)) return isTms ? "TN South" : "South Tennessee";
  if (FCC_NORTH_TENNESSEE_ZIP_PREFIXES.has(prefix3)) return isTms ? "TN North" : "North Tennessee";

  return null;
}

export function getFccInteractionRegion(
  state: string | null | undefined,
  postalCode?: string | null,
  serviceLine?: ServiceLine | null
): string | null {
  const normalizedState = (state ?? "").trim().toUpperCase();

  if (normalizedState === "TX") {
    return isFccTxSanAntonioZip(postalCode) ? "San Antonio" : "Austin";
  }

  if (normalizedState === "CO") {
    const coRegion = getFccInteractionCoRegion(postalCode);

    if (coRegion !== null) return coRegion;
  }

  if (normalizedState === "TN") {
    const tnRegion = getFccInteractionTnRegion(postalCode, serviceLine ?? null);

    if (tnRegion !== null) return tnRegion;
  }

  const flatRegion = FCC_INTERACTION_REGION_BY_STATE[normalizedState];

  return flatRegion ?? null;
}
